import {
  AcceptInvitationRequestV1,
  ConsoleUserListResponseV1,
  DeactivatedConsoleUserResponseV1,
  GrantConsoleUserPermissionsRequestV1,
  IdentityProviderSettingsResponseV1,
  IdentityProviderTestResponseV1,
  IdentityProviderViewV1,
  InvitationIssuedResponseV1,
  InvitationListResponseV1,
  InvitationViewV1,
  InviteConsoleUserRequestV1,
  LoginRequestV1,
  LoginStepResponseV1,
  MfaCodeRequestV1,
  NewPasswordRequestV1,
  OperatorScopedRequestV1,
  OperatorScopeQueryV1,
  PasswordResetAcceptedV1,
  PasswordResetConfirmV1,
  PasswordResetRequestV1,
  PutIdentityProviderRequestV1,
  SessionStartedResponseV1,
  SetConsoleUserRoleRequestV1,
  SsoStartRequestV1,
  SsoStartResponseV1,
  TwoFactorSetupBeginResponseV1,
} from "@strust/contracts";
import { z } from "zod";
import { defineEndpoint } from "../../../shared/api/endpoint.ts";

// Every operation of explorer-access, declared with the contract schemas (task 15.6-15.8).

const NoContent = z.undefined();

/**
 * A started session (`200`), or the step a right password still needs (`202 { next_step }`:
 * the authenticator code or its mandatory set-up, change add-console-account-settings; the new
 * password of a temporary one, change adopt-installation-per-customer).
 */
export const SessionOrLoginStep = z.union([SessionStartedResponseV1, LoginStepResponseV1]);

export const loginEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/login",
  body: LoginRequestV1,
  response: SessionOrLoginStep,
});
/** The code step of a password login (bound to this browser by the HttpOnly login-step cookie). */
export const loginCodeEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/login/mfa",
  body: MfaCodeRequestV1,
  response: SessionStartedResponseV1,
});
/**
 * The new password that replaces a temporary one at the first login (bound to this browser by the
 * login-step cookie): a session, or the next step (the mandatory authenticator set-up).
 */
export const loginNewPasswordEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/login/new-password",
  body: NewPasswordRequestV1,
  response: SessionOrLoginStep,
});
export const beginLoginTwoFactorSetupEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/login/two-factor-setup/begin",
  response: TwoFactorSetupBeginResponseV1,
});
export const confirmLoginTwoFactorSetupEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/login/two-factor-setup/confirm",
  body: MfaCodeRequestV1,
  response: SessionStartedResponseV1,
});
export const ssoStartEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/sso/start",
  body: SsoStartRequestV1,
  response: SsoStartResponseV1,
});
export const logoutEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/logout",
  response: NoContent,
});
// Self-service password reset (flag iam.password-reset; add-console-account-settings D14).
export const passwordResetRequestEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/password-reset",
  body: PasswordResetRequestV1,
  response: PasswordResetAcceptedV1,
});
export const passwordResetConfirmEndpoint = defineEndpoint({
  method: "POST",
  path: "/auth/password-reset/confirm",
  body: PasswordResetConfirmV1,
  response: NoContent,
});
export const inspectInvitationEndpoint = defineEndpoint({
  method: "GET",
  path: "/invitations/:token",
  response: InvitationViewV1,
});
export const acceptInvitationEndpoint = defineEndpoint({
  method: "POST",
  path: "/invitations/:token/accept",
  body: AcceptInvitationRequestV1,
  response: SessionOrLoginStep,
});

export const listConsoleUsersEndpoint = defineEndpoint({
  method: "GET",
  path: "/console-users",
  query: OperatorScopeQueryV1,
  response: ConsoleUserListResponseV1,
});
export const listInvitationsEndpoint = defineEndpoint({
  method: "GET",
  path: "/console-users/invitations",
  query: OperatorScopeQueryV1,
  response: InvitationListResponseV1,
});
export const inviteConsoleUserEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users",
  body: InviteConsoleUserRequestV1,
  response: InvitationIssuedResponseV1,
});
export const revokeInvitationEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users/invitations/:id/revoke",
  body: OperatorScopedRequestV1,
  response: NoContent,
});
export const setRoleEndpoint = defineEndpoint({
  method: "PUT",
  path: "/console-users/:id/roles",
  body: SetConsoleUserRoleRequestV1,
  response: NoContent,
});
export const grantPermissionsEndpoint = defineEndpoint({
  method: "PUT",
  path: "/console-users/:id/permissions",
  body: GrantConsoleUserPermissionsRequestV1,
  response: NoContent,
});
export const deactivateUserEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users/:id/deactivate",
  body: OperatorScopedRequestV1,
  response: DeactivatedConsoleUserResponseV1,
});
export const unlockUserEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users/:id/unlock",
  body: OperatorScopedRequestV1,
  response: NoContent,
});
export const passwordSetupInvitationEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users/:id/password-setup-invitation",
  body: OperatorScopedRequestV1,
  response: InvitationIssuedResponseV1,
});

/** Two-factor reset with a single-use recovery invitation (flag `iam.two-factor`). */
export const resetTwoFactorEndpoint = defineEndpoint({
  method: "POST",
  path: "/console-users/:id/two-factor/reset",
  body: OperatorScopedRequestV1,
  response: InvitationIssuedResponseV1,
});

export const identityProviderSettingsEndpoint = defineEndpoint({
  method: "GET",
  path: "/organization/identity-provider",
  response: IdentityProviderSettingsResponseV1,
});
export const putIdentityProviderEndpoint = defineEndpoint({
  method: "PUT",
  path: "/organization/identity-provider",
  body: PutIdentityProviderRequestV1,
  response: IdentityProviderViewV1,
});
export const testIdentityProviderEndpoint = defineEndpoint({
  method: "POST",
  path: "/organization/identity-provider/test",
  response: IdentityProviderTestResponseV1,
});
export const activateIdentityProviderEndpoint = defineEndpoint({
  method: "POST",
  path: "/organization/identity-provider/activate",
  response: IdentityProviderViewV1,
});
export const deactivateIdentityProviderEndpoint = defineEndpoint({
  method: "POST",
  path: "/organization/identity-provider/deactivate",
  response: IdentityProviderViewV1,
});
